import { useState } from "react";
import { MessageSquare, Send, X, Bug, Lightbulb, Zap, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

const feedbackSchema = z.object({
  title: z.string().min(1, "Title is required").max(255, "Title must be less than 255 characters"),
  description: z.string().min(10, "Description must be at least 10 characters").max(2000, "Description must be less than 2000 characters"),
  category: z.enum(["bug", "feature_request", "improvement", "general"]),
  priority: z.enum(["low", "medium", "high", "critical"]),
});

type FeedbackFormData = z.infer<typeof feedbackSchema>;

const categoryIcons = {
  bug: Bug,
  feature_request: Lightbulb,
  improvement: Zap,
  general: MessageCircle,
};

const categoryLabels = {
  bug: "Bug Report",
  feature_request: "Feature Request",
  improvement: "Improvement",
  general: "General Feedback",
};

const priorityColors = {
  low: "text-green-600 dark:text-green-400",
  medium: "text-yellow-600 dark:text-yellow-400",
  high: "text-orange-600 dark:text-orange-400",
  critical: "text-red-600 dark:text-red-400",
};

interface FeedbackButtonProps {
  className?: string;
  variant?: "default" | "outline" | "ghost";
  size?: "default" | "sm" | "lg" | "icon";
}

export function FeedbackButton({ 
  className = "", 
  variant = "outline", 
  size = "sm" 
}: FeedbackButtonProps) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      title: "",
      description: "",
      category: "general",
      priority: "medium",
    },
  });

  const submitFeedback = useMutation({
    mutationFn: async (data: FeedbackFormData) => {
      const feedbackData = {
        ...data,
        currentPage: window.location.pathname,
      };
      return apiRequest("POST", "/api/feedback", feedbackData);
    },
    onSuccess: () => {
      toast({
        title: "Feedback submitted successfully!",
        description: "Thank you for your feedback. We'll review it and get back to you if needed.",
      });
      form.reset();
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["/api/feedback"] });
    },
    onError: (error: any) => {
      console.error("Feedback submission error:", error);
      toast({
        title: "Failed to submit feedback",
        description: error.message || "There was an error submitting your feedback. Please try again.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: FeedbackFormData) => {
    submitFeedback.mutate(data);
  };

  const CategoryIcon = categoryIcons[form.watch("category") || "general"];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={`gap-2 ${className}`}
          title="Send Feedback"
        >
          <MessageSquare className="h-4 w-4" />
          {size !== "icon" && "Feedback"}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Send Feedback
          </DialogTitle>
          <DialogDescription>
            Help us improve the platform by sharing your thoughts, reporting bugs, or suggesting new features.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select feedback category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.entries(categoryLabels).map(([value, label]) => {
                        const Icon = categoryIcons[value as keyof typeof categoryIcons];
                        return (
                          <SelectItem key={value} value={value}>
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              {label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Priority</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select priority level" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="low">
                        <span className={priorityColors.low}>Low</span>
                      </SelectItem>
                      <SelectItem value="medium">
                        <span className={priorityColors.medium}>Medium</span>
                      </SelectItem>
                      <SelectItem value="high">
                        <span className={priorityColors.high}>High</span>
                      </SelectItem>
                      <SelectItem value="critical">
                        <span className={priorityColors.critical}>Critical</span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <CategoryIcon className="h-4 w-4" />
                    Title
                  </FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Brief summary of your feedback..." 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Please provide detailed information about your feedback. For bugs, include steps to reproduce. For feature requests, describe the desired functionality."
                      className="min-h-[100px] resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={submitFeedback.isPending}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitFeedback.isPending}
                className="gap-2"
              >
                <Send className="h-4 w-4" />
                {submitFeedback.isPending ? "Sending..." : "Send Feedback"}
              </Button>
            </div>
          </form>
        </Form>

        <div className="text-xs text-muted-foreground mt-4 p-3 bg-muted/50 rounded-md">
          <p className="font-medium">Privacy Notice:</p>
          <p>Your feedback will be sent to our development team via Trello. We may contact you for follow-up questions if needed.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}